import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, VAULT_PATH } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { toast } from "sonner";

const COUNTRIES = [
  { code: "GLOBAL", name: "Global" }, { code: "US", name: "USA" },
  { code: "FR", name: "France" }, { code: "GB", name: "UK" },
  { code: "IN", name: "India" }, { code: "BR", name: "Brazil" },
  { code: "JP", name: "Japan" }, { code: "DE", name: "Germany" },
  { code: "KR", name: "S. Korea" }, { code: "MX", name: "Mexico" },
];

export default function SocialStudio() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [country, setCountry] = useState("GLOBAL");
  const [tweets, setTweets] = useState(null);
  const [shorts, setShorts] = useState(null);
  const [loadingT, setLoadingT] = useState(false);
  const [loadingS, setLoadingS] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("tweets");

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        setChecking(false);
      } catch {
        navigate(`${VAULT_PATH}/login`, { replace: true });
      }
    })();
  }, [navigate]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/social/history");
      setHistory(data);
    } catch (e) { console.warn("social history load failed", e); }
  };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect; referenced values are stable module imports/constants
  useEffect(() => { if (!checking) loadHistory(); }, [checking]);

  const genTweets = async () => {
    setLoadingT(true);
    try {
      const { data } = await api.post("/admin/social/tweets", { country, count: 5 });
      setTweets(data);
      loadHistory();
      toast.success(`${data.count} tweets generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    }
    setLoadingT(false);
  };

  const genShorts = async () => {
    setLoadingS(true);
    try {
      const { data } = await api.post("/admin/social/shorts", { country, count: 3, style: "explainer" });
      setShorts(data);
      loadHistory();
      toast.success(`${data.count} scripts generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    }
    setLoadingS(false);
  };

  const copyText = (t) => {
    navigator.clipboard.writeText(t).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed")
    );
  };

  const tweetToFull = (t) => `${t.text}\n\n${(t.hashtags || []).join(" ")}`;

  const shortToFull = (s) =>
    `TITLE: ${s.title}\n\nHOOK: ${s.hook}\n\n${s.body.map((b, i) => `${i + 1}. ${b}`).join("\n")}\n\nCTA: ${s.cta}\n\n${(s.hashtags || []).join(" ")}`;

  if (checking) return (
    <div className="bg-white min-h-screen"><Header /><div className="mx-auto max-w-[1400px] px-6 py-20 font-mono text-xs uppercase tracking-widest text-[#52525b]">Loading…</div></div>
  );

  return (
    <div data-testid="social-studio" className="bg-white min-h-screen text-[#0a0a0a]">
      <Seo title="Social Studio — Vault" description="Admin content generator" />
      <Header />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <div>
              <div className="overline text-[#52525b]">Vault · Social Studio</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                One click.<br /><span className="text-[#ff3b30]">Five tweets. Three Shorts.</span>
              </h1>
              <p className="mt-4 font-mono text-xs text-[#52525b] max-w-[70ch]">
                Powered by Claude Sonnet 4.6 · pulls today's top trends per country · outputs copy-paste-ready assets.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="overline text-[#52525b]">Country</label>
              <select
                data-testid="social-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="border border-black px-3 py-2 font-mono text-xs uppercase tracking-widest bg-white"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex border border-black mb-8">
            <button
              data-testid="tab-tweets"
              onClick={() => setTab("tweets")}
              className={`flex-1 overline py-3 border-r border-black ${tab === "tweets" ? "bg-black text-white" : "bg-white hover:bg-[#f4f4f5]"}`}
            >
              Tweets · X
            </button>
            <button
              data-testid="tab-shorts"
              onClick={() => setTab("shorts")}
              className={`flex-1 overline py-3 ${tab === "shorts" ? "bg-black text-white" : "bg-white hover:bg-[#f4f4f5]"}`}
            >
              YouTube Shorts scripts
            </button>
          </div>

          {tab === "tweets" && (
            <div>
              <button
                data-testid="gen-tweets-btn"
                onClick={genTweets}
                disabled={loadingT}
                className="hard-shadow border border-black bg-[#ff3b30] text-white px-6 py-3 overline disabled:opacity-50"
              >
                {loadingT ? "Generating…" : "Generate 5 tweets"}
              </button>
              {tweets && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 border-t border-l border-black">
                  {tweets.items.map((t, i) => (
                    <div key={i} className="border-r border-b border-black p-5" data-testid={`tweet-${i}`}>
                      <div className="overline text-[#52525b]">Tweet #{i + 1} · {t.trend_term}</div>
                      <p className="mt-3 text-[15px] leading-relaxed whitespace-pre-wrap">{t.text}</p>
                      <div className="mt-3 font-mono text-xs text-[#3f3f46]">{(t.hashtags || []).join(" ")}</div>
                      <button
                        data-testid={`copy-tweet-${i}`}
                        onClick={() => copyText(tweetToFull(t))}
                        className="mt-4 border border-black bg-white text-black px-4 py-2 overline text-xs hover:bg-black hover:text-white"
                      >
                        Copy →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "shorts" && (
            <div>
              <button
                data-testid="gen-shorts-btn"
                onClick={genShorts}
                disabled={loadingS}
                className="hard-shadow border border-black bg-[#ff3b30] text-white px-6 py-3 overline disabled:opacity-50"
              >
                {loadingS ? "Generating…" : "Generate 3 Shorts scripts"}
              </button>
              {shorts && (
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 border-t border-l border-black">
                  {shorts.items.map((s, i) => (
                    <div key={i} className="border-r border-b border-black p-5" data-testid={`short-${i}`}>
                      <div className="overline text-[#52525b]">Short #{i + 1} · {s.trend_term}</div>
                      <div className="mt-3 font-display font-extrabold text-xl tracking-tighter leading-tight">{s.title}</div>
                      <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-[#ff3b30]">Hook</div>
                      <div className="mt-1 italic">&ldquo;{s.hook}&rdquo;</div>
                      <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-[#52525b]">Body</div>
                      <ol className="mt-1 space-y-1 text-sm list-decimal list-inside">
                        {s.body.map((b) => <li key={b}>{b}</li>)}
                      </ol>
                      <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-[#52525b]">CTA</div>
                      <div className="mt-1 text-sm">{s.cta}</div>
                      <div className="mt-3 font-mono text-xs text-[#3f3f46]">{(s.hashtags || []).join(" ")}</div>
                      <button
                        data-testid={`copy-short-${i}`}
                        onClick={() => copyText(shortToFull(s))}
                        className="mt-4 border border-black bg-white text-black px-4 py-2 overline text-xs hover:bg-black hover:text-white"
                      >
                        Copy full script →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-16 border-t border-black pt-8">
            <div className="overline text-[#52525b] mb-4">Recent generations</div>
            {history.length === 0 ? (
              <div className="font-mono text-xs text-[#52525b]">None yet.</div>
            ) : (
              <ul className="divide-y divide-black/15">
                {history.slice(0, 10).map((h) => (
                  <li key={h.id} className="py-2 font-mono text-xs flex justify-between">
                    <span>{h.type.toUpperCase()} · {h.country} · {h.count} items</span>
                    <span className="text-[#52525b]">{new Date(h.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
